import { isEntirePolygonSelected } from '../helpers/storey-selection.helpers';
import type { Map as MapboxMap } from 'mapbox-gl';
import {
  Observable,
  combineLatest,
  Subscription,
  BehaviorSubject,
  distinctUntilChanged,
} from 'rxjs';
import { Coordinate } from '@shared/models/geography.model';
import { Storey, DraftShape } from '@shared/models/design/storey.model';
import { designGuards } from '@shared/helpers/design/design-typeguards.helpers';
import {
  applyGeoJsonToSource,
  getMapStoreyPolygonsFromStoreys,
  getOtherBuildingMapPolygons,
} from '../helpers/map-features.helpers';
import {
  clearHoveredPoint as clearHoveredPointHelper,
  applyPointMove as applyPointMoveHelper,
  renderBuildingPolygons,
  setHoveredPoint as setHoveredPointHelper,
  updatePointSources as updatePointSourcesHelper,
  updateLocationPinSource,
} from '../helpers/map.helpers';
import type { ApplyPointMoveOptions } from '../helpers/map.helpers';
import {
  SourceId,
  MapLayerIds,
  PIN_MODE_HIDDEN_LAYER_IDS,
} from '../helpers/map-config.constants';
import {
  MapHoverStateType,
  type MapHoverState,
  type MapSelections,
  type MapSelectionObservables,
  type MapStateObservables,
  type PointPayload,
  type MapDraftCursorState,
  type ShapeTranslatePayload,
} from '../models/map-observables.models';
import { isEqualPoints } from '@shared/helpers/geometry/geometry.helpers';
import { isClickAnywhereDrawMode } from '@shared/helpers/design/storey.helpers';
import { CLICK_ANYWHERE_OTHER_BUILDINGS_OPACITY } from '../map.constants';
import type { Polygon } from 'geojson';
import {
  BUILDING_CLIP_LAYER_ID,
  BUILDING_CLIP_SOURCE_ID,
  computeBuildingMaskPolygons,
  ensureBuildingClipLayer,
  updateBuildingClipSource,
  WORLD_BUILDING_CLIP_POLYGON,
  applyOtherBuildingsLayerOpacity,
} from './mask.helpers';
import { InsertPlaceholderController } from './insert-placeholder.controller';
import { LocationPinCursorState } from '../map-event-handler/location-pin.controller';
import { convertMetricOffsetToCoordinate } from '@shared/helpers/geometry/coordinates.helpers';
import { getStoreyElevation } from '@shared/helpers/design/storey.helpers';
import { projectLngLatAtElevation } from '../helpers/map.helpers';
import {
  getLineDragCursor,
  getSegmentAngleRadians,
  type LineDragCursor,
} from '../helpers/line-drag-cursor.helpers';
import { getLocationPinOpacity } from '../helpers/location-pin-visibility.helpers';
import { ROTATE_CURSOR } from '../helpers/rotate-cursor.helpers';
import { getViewRotationHoverCursor } from '../helpers/view-rotation-cursor.helpers';
import { applyShapeTranslationPreview } from '../threejs/translation-preview.helpers';

type MapRendererDependencies = {
  selections: MapSelectionObservables;
  hoverState$: BehaviorSubject<MapHoverState | undefined>;
  pointMove$: Observable<PointPayload | undefined>;
  draftCursorState$: BehaviorSubject<MapDraftCursorState>;
  hasPointInsert?: boolean;
  shapeTranslation$?: Observable<ShapeTranslatePayload | undefined>;
  buildingRotationHover$?: Observable<boolean>;
  locationPinCursor$?: BehaviorSubject<LocationPinCursorState>;
};

type PointRenderState = {
  storeys: Storey[];
  draftShape: DraftShape | undefined;
  selectionSnapshot: MapSelections;
  hoverState: MapHoverState | undefined;
  location: Coordinate;
  locationPinMode: boolean;
};

export class MapRenderer {
  private isReadonly = false;
  private hoveredPointId: string | number | null = null;
  private hoveredDraftPointId: string | number | null = null;
  private lastHoverState: MapHoverState | undefined;
  private draftCursorState: MapDraftCursorState = {
    active: false,
    suppressed: false,
  };
  private activePointMove: PointPayload | undefined;
  private isPointDragActive = false;
  private pendingPointMove: PointPayload | undefined;
  private pendingPointMoveVersion: number | undefined;
  private latestPointRenderState: PointRenderState | undefined;
  private locationPinCursorState: LocationPinCursorState = {
    active: false,
    hover: false,
    dragging: false,
    canTranslate: false,
  };

  private location: Coordinate;

  private buildingGeoJson: ReturnType<typeof getMapStoreyPolygonsFromStoreys>;
  private hasPointInsert: boolean = false;

  private shapeTranslation: ShapeTranslatePayload | undefined;
  private buildingRotationHovered = false;
  private shapeTranslationBaseStoreys: Storey[] | undefined;
  /** Locks cursor while middle-mouse / space navigation pan is active. */
  private navigationCursor: 'grabbing' | undefined;
  /** Last Figma-like segment-drag cursor from line hover (kept during segment drag). */
  private lineDragCursor: LineDragCursor | undefined;
  /** Active canvas tool; Move shows its cursor only over the selected building. */
  private designMapTool: 'build' | 'draw' | 'moveBuilding' = 'build';
  /** Whole-building outline selection — hover over the volume shows move. */
  private isBuildingOutlineSelected = false;
  /** Building id under the pointer (selected or sibling). */
  private hoveredBuildingId: string | undefined;
  /** Space held — grab cursor without changing the tool chip. */
  private spacePanActive = false;
  /** Complete new-building footprint follows the pointer instead of a crosshair. */
  private buildingFootprintTemplateActive = false;

  private latestStoreys: Storey[] = [];
  private storeysVersion = 0;
  private latestSelectedStoreyId: Storey['id'] | undefined;

  private readonly insertPlaceholderController: InsertPlaceholderController;

  private readonly subscription = new Subscription();
  private externalBuildingMask?: Polygon[];
  private buildingClipApplyKey?: string;
  private detachStyleDataListener?: () => void;
  // false = clip active (buildings hidden near point), true = clip disabled (buildings shown)
  private showNearbyBuildings = false;
  /** When true, clip every Mapbox 3D model (background map unticked). */
  private hideAllMapboxBuildings = false;
  private lastContextBuildingsOpacity: number | undefined;

  /**
   * Create a renderer responsible for synchronising map sources with state.
   * @param map {@link Map} instance used for rendering
   * @param state {@link MapStateObservables} data sources
   * @param dependencies Additional streams required for rendering
   */
  constructor(
    private readonly map: MapboxMap,
    private readonly state: MapStateObservables,
    {
      selections,
      hoverState$,
      pointMove$,
      draftCursorState$,
      hasPointInsert = false,
      shapeTranslation$,
      buildingRotationHover$,
      locationPinCursor$,
    }: MapRendererDependencies,
  ) {
    this.hasPointInsert = hasPointInsert;
    const initialLocation = state.location$.getValue();
    this.location = initialLocation;
    this.latestStoreys = state.storeys$.getValue();
    this.latestSelectedStoreyId = state.selectedStoreyId$.getValue();
    this.buildingGeoJson = getMapStoreyPolygonsFromStoreys(
      [],
      undefined,
      initialLocation,
    );
    this.insertPlaceholderController = new InsertPlaceholderController({
      map: this.map,
      getBuildingGeoJson: () => this.buildingGeoJson,
      getLinkedStoreyLevels: (payload) => this.getLinkedStoreyLevels(payload),
    });
    this.setupStoreySync();
    this.setupOtherBuildingsSubscription();
    this.setupHoverSubscription(hoverState$);
    this.setupOutlineMoveCursorSubscription();
    this.setupPointMoveSubscription(pointMove$);
    this.setupDraftCursorSubscription(draftCursorState$);
    this.setupShapeTranslationSubscription(shapeTranslation$);
    if (buildingRotationHover$) {
      this.subscription.add(
        buildingRotationHover$.subscribe((hovered) => {
          this.buildingRotationHovered = hovered;
          this.updateCursor();
        }),
      );
    }
    this.setupLocationPinCursorSubscription(locationPinCursor$);
    this.setupLocationPinVisibilitySubscription();
    this.setupLocationPinSourceSubscription();
    this.setupPointRenderSubscription(selections, hoverState$);
    this.setupStyleDataListener();
  }

  /**
   * Observe core storey signals and keep polygon sources aligned.
   */
  private setupStoreySync(): void {
    this.subscription.add(
      combineLatest([
        this.state.storeys$,
        this.state.selectedStoreyId$,
        this.state.location$,
      ]).subscribe(([storeys, selectedStoreyId, location]) => {
        this.storeysVersion += 1;
        this.location = location;
        this.latestStoreys = storeys;
        this.latestSelectedStoreyId = selectedStoreyId ?? undefined;
        if (this.shouldHoldMapboxSourceUpdates()) {
          return;
        }
        this.syncBuildingPolygons(storeys, selectedStoreyId);
      }),
    );
  }

  /**
   * Render non-selected buildings as static footprints so multiple buildings
   * stay visible while one is edited (DEV-2576).
   */
  private setupOtherBuildingsSubscription(): void {
    this.subscription.add(
      combineLatest([
        this.state.otherBuildings$,
        this.state.storeys$,
        this.state.location$,
      ]).subscribe(([buildings, selectedStoreys, location]) => {
        const features = getOtherBuildingMapPolygons(
          buildings,
          selectedStoreys,
          location,
        );
        applyGeoJsonToSource(this.map, SourceId.OtherBuildings, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features },
        });
        this.applyPinModeLayerVisibility();
        this.applyExternalBuildingMask(
          computeBuildingMaskPolygons(
            selectedStoreys,
            this.latestPointRenderState?.draftShape,
            location,
            buildings,
          ),
        );
      }),
    );
  }

  /**
   * Track hover state mutations so cursor + highlights stay responsive.
   */
  private setupHoverSubscription(
    hoverState$: BehaviorSubject<MapHoverState | undefined>,
  ): void {
    this.subscription.add(
      hoverState$.subscribe((hoverState) => {
        this.lastHoverState = hoverState;
        this.applyHoverState(hoverState);
        this.updateCursor();
      }),
    );
  }

  /**
   * Show the move cursor when the selected building's outline is hovered.
   */
  private setupOutlineMoveCursorSubscription(): void {
    this.subscription.add(
      combineLatest([
        this.state.isBuildingOutlineSelected$,
        this.state.hoveredBuildingId$,
      ]).subscribe(([isOutlineSelected, hoveredBuildingId]) => {
        this.isBuildingOutlineSelected = isOutlineSelected;
        this.hoveredBuildingId = hoveredBuildingId;
        this.updateCursor();
      }),
    );
  }

  /**
   * React to active point drag payloads from the event handler.
   */
  private setupPointMoveSubscription(
    pointMove$: Observable<PointPayload | undefined>,
  ): void {
    this.subscription.add(
      pointMove$.subscribe((payload) => {
        const wasDragging = this.isPointDragActive;
        this.isPointDragActive = Boolean(payload);
        if (!payload || payload.isDraft) {
          const previousMove = this.activePointMove;
          if (previousMove?.isInsertDrag) {
            // Ensure insert placeholders are removed after a completed add-point drag.
            this.insertPlaceholderController.clear(previousMove);
          }
          this.insertPlaceholderController.releaseActivePlaceholder();
          this.activePointMove = undefined;
          this.pendingPointMove = undefined;
          this.pendingPointMoveVersion = undefined;
          if (wasDragging !== this.isPointDragActive) {
            this.updateCursor();
          }
          return;
        }
        this.pendingPointMove = undefined;
        this.pendingPointMoveVersion = undefined;
        const previousMove = this.activePointMove;
        const wasInsertDrag = previousMove?.isInsertDrag;
        if (payload.isInsertDrag && !wasInsertDrag) {
          this.insertPlaceholderController.clear(payload);
        }
        this.activePointMove = payload;

        // this.applyPointMove(payload);
        if (wasDragging !== this.isPointDragActive) {
          this.updateCursor();
        }
      }),
    );
  }

  /**
   * Mirror draft cursor suppression changes.
   */
  private setupDraftCursorSubscription(
    draftCursorState$: BehaviorSubject<MapDraftCursorState>,
  ): void {
    this.subscription.add(
      draftCursorState$.subscribe((state) => {
        this.setDraftCursorState(state);
      }),
    );
  }

  /**
   * Keep translation previews and rendered geometry aligned.
   */
  private setupShapeTranslationSubscription(
    shapeTranslation$?: Observable<ShapeTranslatePayload | undefined>,
  ): void {
    if (!shapeTranslation$) return;
    this.subscription.add(
      shapeTranslation$.subscribe((payload) => {
        const hadActiveTranslation = Boolean(this.shapeTranslation);
        this.shapeTranslation = payload;
        if (!hadActiveTranslation && payload) {
          this.shapeTranslationBaseStoreys = this.latestStoreys;
        }
        if (!payload) {
          this.shapeTranslationBaseStoreys = undefined;
        }
        this.updateCursor();
        // Mapbox symbol/clip placement crashes with "feature index out of bounds"
        // if GeoJSON sources change every pointer sample. Preview lives in the
        // custom overlay; sync Mapbox sources once when the drag ends.
        if (payload) return;
        this.renderVerticesFromLatestState();
        this.syncBuildingPolygons(
          this.latestStoreys,
          this.latestSelectedStoreyId,
        );
      }),
    );
  }

  private setupLocationPinCursorSubscription(
    locationPinCursor$?: BehaviorSubject<LocationPinCursorState>,
  ): void {
    if (!locationPinCursor$) return;
    this.subscription.add(
      locationPinCursor$.subscribe((state) => {
        this.locationPinCursorState = state;
        this.applyLocationPinAppearance();
        this.updateCursor();
      }),
    );
  }

  /**
   * Hide building overlays while the location pin replaces them.
   */
  private setupLocationPinVisibilitySubscription(): void {
    this.subscription.add(
      this.state.locationPinMode$.subscribe(() => {
        this.applyPinModeLayerVisibility();
      }),
    );
  }

  /**
   * Location pin replaces building overlays; keep those layers off otherwise.
   */
  private applyPinModeLayerVisibility(): void {
    const shouldShowBuildings = !this.state.locationPinMode$.getValue();
    PIN_MODE_HIDDEN_LAYER_IDS.forEach((layerId) => {
      this.setLayerVisibility(layerId, shouldShowBuildings);
    });
  }

  /**
   * Keep the location pin source fresh against SMHI-grade location shifts.
   */
  private setupLocationPinSourceSubscription(): void {
    this.subscription.add(
      combineLatest([
        this.state.location$,
        this.state.locationPinMode$,
      ]).subscribe(([location, isPinMode]) => {
        updateLocationPinSource(this.map, location, isPinMode);
      }),
    );
  }

  /**
   * Re-render vertices when store, selection, or hover snapshots change.
   */
  private setupPointRenderSubscription(
    selections: MapSelectionObservables,
    hoverState$: BehaviorSubject<MapHoverState | undefined>,
  ): void {
    this.subscription.add(
      combineLatest([
        this.state.storeys$,
        this.state.draftShape$,
        this.state.selectedStoreyId$,
        selections.pointIndexes$,
        selections.shapeId$,
        selections.draftShape$,
        hoverState$.pipe(distinctUntilChanged(areHoverStatesStructurallyEqual)),
        this.state.location$,
        this.state.locationPinMode$,
      ]).subscribe(
        ([
          storeys,
          draftShape,
          storeyId,
          pointIndexes,
          shapeId,
          draftShapeSelected,
          hoverState,
          location,
          locationPinMode,
        ]) => {
          this.location = location;
          const selectionSnapshot: MapSelections = {
            storeyId: storeyId ?? undefined,
            pointIndexes: pointIndexes ?? undefined,
            shapeId: shapeId ?? undefined,
            draftShape: draftShapeSelected ?? false,
          };
          this.latestPointRenderState = {
            storeys,
            draftShape,
            selectionSnapshot,
            hoverState,
            location: this.location,
            locationPinMode,
          };
          this.updateCursor();
          if (this.shouldHoldMapboxSourceUpdates()) return;
          this.renderVerticesFromState(this.latestPointRenderState);
        },
      ),
    );
  }

  /**
   * Ensure clip layers survive style reloads.
   */
  private setupStyleDataListener(): void {
    ensureBuildingClipLayer(this.map);
    const handleStyleData = (): void => {
      this.applyLocationPinAppearance();
      const { ready, created } = ensureBuildingClipLayer(this.map);
      if (!ready) return;
      this.applyExternalBuildingMask(this.externalBuildingMask, created);
      this.lastContextBuildingsOpacity = undefined;
      if (this.latestPointRenderState) {
        this.applyClickAnywhereBuildingFade(this.latestPointRenderState, true);
      }
    };
    this.map.on('styledata', handleStyleData);
    this.detachStyleDataListener = () => {
      this.map.off('styledata', handleStyleData);
    };
  }

  /** Match pin opacity to its pointer interaction state. */
  private applyLocationPinAppearance(): void {
    if (!this.map.getLayer(MapLayerIds.LocationPin)) return;
    const opacity = getLocationPinOpacity(this.locationPinCursorState);
    if (
      this.map.getPaintProperty(MapLayerIds.LocationPin, 'icon-opacity') ===
      opacity
    ) {
      return;
    }
    this.map.setPaintProperty(MapLayerIds.LocationPin, 'icon-opacity', opacity);
  }

  /**
   * Resolve storey levels that should remain linked during drag operations.
   * @param payload {@link PointPayload} describing the active point
   * @returns Array of storey level indices or undefined
   * // TODO: Should not live here?!
   */
  private getLinkedStoreyLevels(payload: PointPayload): number[] | undefined {
    if (payload.linkedStoreyLevels?.length) {
      return payload.linkedStoreyLevels;
    }
    if (!this.latestPointRenderState) return undefined;
    const { storeys, selectionSnapshot } = this.latestPointRenderState;
    const baseStoreyId = selectionSnapshot.storeyId;
    if (!baseStoreyId) return undefined;

    const originPoint = payload.origin;
    if (!originPoint) return undefined;

    const baseIndex = storeys.findIndex((storey) => storey.id === baseStoreyId);
    if (baseIndex < 0) return undefined;
    const baseStorey = storeys[baseIndex];
    if (designGuards.is.mezzanineStorey(baseStorey)) {
      // Henrik vill inte att mezzaniner påverkar ovanliggande våningar under drag.
      return [baseIndex];
    }

    const findMatchingShape = (
      storey: Storey,
    ): DraftShape | Storey['shapes'][number] | undefined => {
      const byId = storey.shapes.find((shape) => shape.id === payload.shapeId);
      if (byId?.points?.[payload.index]) return byId;
      return storey.shapes.find((shape) => {
        if (!shape?.points?.length) return false;
        if (shape.points.length !== (payload.shape.points?.length ?? 0)) {
          return false;
        }
        const candidatePoint = shape.points[payload.index];
        return Boolean(
          candidatePoint && isEqualPoints(candidatePoint, originPoint),
        );
      });
    };

    const levels: number[] = [];
    for (let index = baseIndex; index < storeys.length; index += 1) {
      const storey = storeys[index];
      if (!storey) break;
      const shape = findMatchingShape(storey);
      if (!shape) break;
      const candidatePoint = shape.points?.[payload.index];
      if (!candidatePoint || !isEqualPoints(candidatePoint, originPoint)) break;
      levels.push(index);
    }

    return levels.length ? levels : undefined;
  }

  /**
   * Apply hover state visuals across persisted and draft vertices.
   * @param hoverState {@link MapHoverState} describing current pointer focus
   */
  private applyHoverState(hoverState: MapHoverState | undefined): void {
    if (!hoverState) {
      this.hoveredPointId = clearHoveredPointHelper(
        this.map,
        this.hoveredPointId,
        SourceId.Points,
      );
      this.hoveredDraftPointId = clearHoveredPointHelper(
        this.map,
        this.hoveredDraftPointId,
        SourceId.DraftPoints,
      );
      return;
    }

    if (hoverState.type === MapHoverStateType.Point) {
      // Clear draft hover if any
      this.hoveredDraftPointId = clearHoveredPointHelper(
        this.map,
        this.hoveredDraftPointId,
        SourceId.DraftPoints,
      );
      const pointId = `${hoverState.shapeId}:${hoverState.index}`;
      this.hoveredPointId = setHoveredPointHelper(
        this.map,
        pointId,
        this.hoveredPointId,
        SourceId.Points,
      );
      return;
    }

    if (hoverState.type === MapHoverStateType.DraftPoint) {
      // Clear regular hover if any
      this.hoveredPointId = clearHoveredPointHelper(
        this.map,
        this.hoveredPointId,
        SourceId.Points,
      );
      // Draft points use the same feature ID format: `${shape.id}:${index}`
      const shapeId = hoverState.shapeId ?? 'undefined';
      const pointId = `${shapeId}:${hoverState.index}`;
      this.hoveredDraftPointId = setHoveredPointHelper(
        this.map,
        pointId,
        this.hoveredDraftPointId,
        SourceId.DraftPoints,
      );
      return;
    }

    // Clear both hover states for other hover types
    this.hoveredPointId = clearHoveredPointHelper(
      this.map,
      this.hoveredPointId,
      SourceId.Points,
    );
    this.hoveredDraftPointId = clearHoveredPointHelper(
      this.map,
      this.hoveredDraftPointId,
      SourceId.DraftPoints,
    );
  }

  /**
   * Apply a live point move payload to the underlying sources.
   * @param payload {@link PointPayload} produced during dragging
   * // TODO: Should not live here: Should just get updated storeys
   */
  private applyPointMove(payload: PointPayload): void {
    if (!payload.shapeId) return;
    // Henrik: Normal drags update storeys state; keep renderer-only patches for insert previews.
    if (!payload.isInsertDrag) return;
    if (payload.isInsertDrag) {
      this.insertPlaceholderController.ensure(payload);
    }
    const levels = this.getLinkedStoreyLevels(payload);
    const pointLevelOverride =
      payload.isInsertDrag && levels?.length ? levels[0] : undefined;
    const moveOptions: ApplyPointMoveOptions = {
      levelsToUpdate: levels,
      pointLevelOverride,
      levelShapeIds: payload.linkedShapeIds,
    };
    if (payload.linkedInsertIndexes) {
      moveOptions.levelInsertIndexes = payload.linkedInsertIndexes;
    }

    applyPointMoveHelper(this.map, payload, this.buildingGeoJson, moveOptions);
  }

  /**
   * Re-render vertices using the most recently captured render state snapshot.
   */
  private renderVerticesFromLatestState(): void {
    if (!this.latestPointRenderState) return;
    if (this.shouldHoldMapboxSourceUpdates()) {
      return;
    }
    this.renderVerticesFromState(this.latestPointRenderState);
    if (this.activePointMove) {
      // this.applyPointMove(this.activePointMove);
    }
  }

  /**
   * Force a point re-render using cached state.
   */
  refreshVertices(): void {
    this.renderVerticesFromLatestState();
  }

  /**
   * Render point sources from an explicit render state payload.
   * @param state {@link PointRenderState} to apply
   */
  private renderVerticesFromState(state: PointRenderState): void {
    const renderState = this.withShapeTranslation(state);
    const shouldShowInsertCandidate =
      this.hasPointInsert && !this.activePointMove?.isInsertDrag;
    updatePointSourcesHelper(
      this.map,
      renderState.storeys,
      renderState.draftShape,
      renderState.selectionSnapshot,
      renderState.hoverState,
      renderState.location,
      {
        suppressPoints: renderState.locationPinMode,
        suppressLines: renderState.locationPinMode,
        hasPointInsert: shouldShowInsertCandidate,
      },
    );

    // TODO: Should not live here: Should just get updated storeys
    if (this.activePointMove) {
      // this.applyPointMove(this.activePointMove);
    }
    this.updateExternalBuildingMask(renderState);
    this.applyClickAnywhereBuildingFade(renderState);
    this.applyPinModeLayerVisibility();
  }

  /**
   * Skip Mapbox GeoJSON (points, fill, clip) while a shape is translating.
   * Those sources feed symbol/model placement; rewriting them mid-frame can
   * throw "feature index out of bounds" in Mapbox's vector-tile loader.
   */
  private shouldHoldMapboxSourceUpdates(): boolean {
    return Boolean(this.shapeTranslation);
  }

  /**
   * Hide Mapbox 3D buildings around every project footprint, including siblings.
   */
  private updateExternalBuildingMask(state: PointRenderState): void {
    const polygons = computeBuildingMaskPolygons(
      state.storeys,
      state.draftShape,
      state.location,
      this.state.otherBuildings$.getValue(),
    );
    this.applyExternalBuildingMask(polygons);
  }

  /**
   * Dim sibling project buildings while the click-anywhere overlay is shown.
   * Keep Mapbox Standard buildings opaque: lowering model opacity exposes
   * occluded geometry and shadows, which looks like a second map underneath.
   */
  private applyClickAnywhereBuildingFade(
    state: PointRenderState,
    force = false,
  ): void {
    const opacity =
      this.state.hasBuildingSelection$.getValue() &&
      isClickAnywhereDrawMode(state.storeys, state.draftShape)
        ? CLICK_ANYWHERE_OTHER_BUILDINGS_OPACITY
        : 1;
    if (!force && this.lastContextBuildingsOpacity === opacity) return;
    this.lastContextBuildingsOpacity = opacity;
    applyOtherBuildingsLayerOpacity(this.map, opacity);
  }

  setShowNearbyBuildings(show: boolean): void {
    if (this.showNearbyBuildings === show) return;
    this.showNearbyBuildings = show;
    this.refreshBuildingClip();
  }

  /**
   * Hide every Mapbox 3D building (background map unticked). Uses a world clip
   * so models cannot poke through an elevated PDF when `show3dObjects` flickers.
   * Reapplies click-anywhere fade so basemap opacity is not left over from the clip.
   */
  setHideAllMapboxBuildings(hide: boolean): void {
    if (this.hideAllMapboxBuildings === hide) return;
    this.hideAllMapboxBuildings = hide;
    this.refreshBuildingClip();
    this.lastContextBuildingsOpacity = undefined;
    if (this.latestPointRenderState) {
      this.applyClickAnywhereBuildingFade(this.latestPointRenderState, true);
    }
  }

  /**
   * Re-assert the nearby-building clip layer (stack order + polygon).
   * Call after basemap config changes that may recreate 3D model layers.
   */
  refreshBuildingClip(): void {
    this.applyExternalBuildingMask(this.externalBuildingMask, true);
  }

  /**
   * Push site clip holes (or the world clip) onto the Mapbox clip source.
   * {@link polygons} is the per-building nearby mask; world clip and nearby-on
   * override it at apply time.
   */
  private applyExternalBuildingMask(
    polygons: Polygon[] | undefined,
    force: boolean = false,
  ): void {
    const maskKey = polygons?.length
      ? JSON.stringify(polygons.map((polygon) => polygon.coordinates))
      : undefined;
    const clipApplyKey = `${this.hideAllMapboxBuildings}:${this.showNearbyBuildings}:${maskKey ?? ''}`;
    if (!force && clipApplyKey === this.buildingClipApplyKey) return;
    this.externalBuildingMask = polygons;
    this.buildingClipApplyKey = clipApplyKey;
    const mapInstance = this.map;
    if (!mapInstance) return;
    if (!ensureBuildingClipLayer(mapInstance).ready) return;
    // Background off → clip the world. Nearby off → clip site. Nearby on → no clip.
    let clipPolygons: Polygon[] | undefined;
    if (this.hideAllMapboxBuildings) {
      clipPolygons = [WORLD_BUILDING_CLIP_POLYGON];
    } else if (!this.showNearbyBuildings) {
      clipPolygons = polygons;
    }
    updateBuildingClipSource(mapInstance, clipPolygons);
  }

  private withShapeTranslation(state: PointRenderState): PointRenderState {
    if (!this.shapeTranslation) return state;
    const baseStoreys = this.shapeTranslationBaseStoreys ?? state.storeys;
    const translatedStoreys = this.getTranslatedStoreys(
      baseStoreys,
      this.shapeTranslation,
    );
    return {
      ...state,
      storeys: translatedStoreys,
    };
  }

  private getTranslatedStoreys(
    storeys: Storey[],
    payload: ShapeTranslatePayload,
  ): Storey[] {
    return applyShapeTranslationPreview(storeys, payload);
  }

  private syncBuildingPolygons(
    storeys: Storey[],
    selectedStoreyId: Storey['id'] | undefined,
  ): void {
    const effectiveStoreys =
      this.shapeTranslation && this.shapeTranslationBaseStoreys
        ? this.getTranslatedStoreys(
            this.shapeTranslationBaseStoreys,
            this.shapeTranslation,
          )
        : storeys;
    this.buildingGeoJson = renderBuildingPolygons(
      this.map,
      effectiveStoreys,
      selectedStoreyId,
      this.location,
    );
  }

  /**
   * Lock the canvas cursor during map navigation (right-drag or middle-mouse pan).
   * Pass `undefined` to return control to hover/draft cursor logic.
   */
  setNavigationCursor(cursor: 'grabbing' | undefined): void {
    this.navigationCursor = cursor;
    this.updateCursor();
  }

  /**
   * Sync Design canvas tool so idle cursor matches the toolbar (e.g. Move → `move`).
   */
  setDesignMapTool(tool: 'build' | 'draw' | 'moveBuilding'): void {
    if (this.designMapTool === tool) return;
    this.designMapTool = tool;
    this.updateCursor();
  }

  /**
   * Space-pan gesture — grab cursor only; does not change the tool chip.
   */
  setSpacePanActive(active: boolean): void {
    if (this.spacePanActive === active) return;
    this.spacePanActive = active;
    this.updateCursor();
  }

  /**
   * Resolve a Figma-like resize cursor for the hovered segment.
   * Uses screen-projected endpoints so the arrow stays perpendicular to the
   * visible edge, including when the map is rotated.
   */
  private resolveLineDragCursor(
    hoverState: Extract<MapHoverState, { type: MapHoverStateType.Line }>,
  ): LineDragCursor | undefined {
    const storey = this.latestStoreys.find(
      (s) => s.id === this.latestSelectedStoreyId,
    );
    if (!storey) return undefined;
    const shape = storey.shapes?.find((s) => s.id === hoverState.shapeId);
    const start = shape?.points?.[hoverState.startIndex];
    const end = shape?.points?.[hoverState.endIndex];
    if (!start || !end) return undefined;

    const location = this.latestPointRenderState?.location ?? this.location;
    const elevation = getStoreyElevation(this.latestStoreys, storey);
    const startScreen = projectLngLatAtElevation(
      this.map,
      convertMetricOffsetToCoordinate(location, start),
      elevation,
    );
    const endScreen = projectLngLatAtElevation(
      this.map,
      convertMetricOffsetToCoordinate(location, end),
      elevation,
    );
    return getLineDragCursor(getSegmentAngleRadians(startScreen, endScreen));
  }

  /**
   * Update the map canvas cursor according to hover and draft states.
   */
  private updateCursor(): void {
    const canvas = this.map.getCanvasContainer();
    if (!canvas) return;

    if (this.navigationCursor) {
      canvas.style.cursor = this.navigationCursor;
      return;
    }

    // Space pan: grab over everything; tool chip stays unchanged.
    if (this.spacePanActive) {
      canvas.style.cursor = 'grab';
      return;
    }

    if (this.shapeTranslation) {
      canvas.style.cursor = this.shapeTranslation.rotation
        ? ROTATE_CURSOR
        : 'grabbing';
      return;
    }

    if (this.buildingRotationHovered) {
      canvas.style.cursor = ROTATE_CURSOR;
      return;
    }

    if (this.locationPinCursorState.dragging) {
      canvas.style.cursor = 'grabbing';
      return;
    }

    if (this.isPointDragActive) {
      // Segment drag keeps the resize cursor; vertex drag uses grabbing.
      canvas.style.cursor =
        this.activePointMove?.isLineSegmentDrag && this.lineDragCursor
          ? this.lineDragCursor
          : 'grabbing';
      return;
    }

    if (
      this.locationPinCursorState.canTranslate &&
      this.locationPinCursorState.active
    ) {
      canvas.style.cursor = 'grab';
      return;
    }

    const hoverState = this.lastHoverState;

    // Show copy cursor when hovering over start point with 3+ points, regardless of Alt key
    if (
      hoverState?.type === MapHoverStateType.DraftPoint &&
      hoverState.canComplete
    ) {
      canvas.style.cursor = 'copy';
      return;
    }

    if (this.draftCursorState.active) {
      if (this.buildingFootprintTemplateActive) {
        canvas.style.cursor = 'none';
        return;
      }
      canvas.style.cursor = this.draftCursorState.suppressed
        ? 'not-allowed'
        : 'crosshair';
      return;
    }

    if (!hoverState) {
      // Keep lineDragCursor across drag-start (hover clears before pointMove$).
      if (this.spacePanActive) {
        canvas.style.cursor = 'grab';
        return;
      }
      if (
        this.shouldShowSelectedBuildingMoveCursor() &&
        this.designMapTool === 'moveBuilding'
      ) {
        canvas.style.cursor = 'move';
        return;
      }
      canvas.style.cursor =
        getViewRotationHoverCursor({
          canRotate: !this.isReadonly,
        }) ?? '';
      return;
    }

    if (hoverState.type === MapHoverStateType.Line) {
      // Mid insert-point uses pointer; elsewhere show Figma-like edge-drag cursor.
      if (hoverState.insertCandidateActive) {
        this.lineDragCursor = undefined;
        canvas.style.cursor = 'pointer';
        return;
      }
      const dragCursor = this.resolveLineDragCursor(hoverState);
      this.lineDragCursor = dragCursor;
      canvas.style.cursor = dragCursor ?? 'pointer';
      return;
    }

    if (hoverState.type === MapHoverStateType.DraftLine) {
      canvas.style.cursor = 'pointer';
      return;
    }

    if (hoverState.type === MapHoverStateType.DraftPoint) {
      canvas.style.cursor = 'pointer';
      return;
    }

    if (hoverState.type === MapHoverStateType.Point) {
      canvas.style.cursor = 'pointer';
      return;
    }

    if (
      hoverState.type === MapHoverStateType.Shape &&
      (this.designMapTool === 'moveBuilding' ||
        (!this.isBuildingOutlineSelected &&
          this.latestPointRenderState &&
          isEntirePolygonSelected(
            this.latestPointRenderState.storeys
              .find(
                (storey) =>
                  storey.id ===
                  this.latestPointRenderState?.selectionSnapshot.storeyId,
              )
              ?.shapes.find((shape) => shape.id === hoverState.shapeId),
            this.latestPointRenderState.selectionSnapshot,
          )))
    ) {
      canvas.style.cursor = 'move';
      return;
    }

    if (hoverState.type === MapHoverStateType.Shape) {
      canvas.style.cursor =
        getViewRotationHoverCursor({
          canRotate: !this.isReadonly,
        }) ?? '';
      return;
    }

    canvas.style.cursor = '';
  }

  /**
   * True when the pointer is over the outline-selected building (not a sibling).
   */
  private shouldShowSelectedBuildingMoveCursor(): boolean {
    if (
      !this.isBuildingOutlineSelected &&
      this.designMapTool !== 'moveBuilding'
    ) {
      return false;
    }
    if (!this.hoveredBuildingId) return false;
    return !this.state.otherBuildings$
      .getValue()
      .some((building) => building.id === this.hoveredBuildingId);
  }

  /**
   * Store the draft cursor state and refresh cursor styling.
   * @param state {@link MapDraftCursorState} to apply
   */
  private setDraftCursorState(state: MapDraftCursorState): void {
    this.draftCursorState = state;
    this.updateCursor();
  }

  setBuildingFootprintTemplateActive(active: boolean): void {
    if (this.buildingFootprintTemplateActive === active) return;
    this.buildingFootprintTemplateActive = active;
    this.updateCursor();
  }

  /**
   * Control whether point insertion cursors should be shown.
   * @param has Boolean flag indicating insert capability
   */
  setHasPointInsert(has: boolean): void {
    if (this.hasPointInsert === has) return;
    this.hasPointInsert = has;
    this.renderVerticesFromLatestState();
  }

  /**
   * Toggle layer visibility if the layer exists on the map.
   * @param layerId {@link MapLayerIds} target
   * @param visible Whether the layer should be visible
   */
  private setLayerVisibility(layerId: MapLayerIds, visible: boolean): void {
    if (!this.map.getLayer(layerId)) return;
    const nextVisibility = visible ? 'visible' : 'none';
    const currentVisibility = this.map.getLayoutProperty(layerId, 'visibility');
    if (currentVisibility === nextVisibility) return;
    this.map.setLayoutProperty(layerId, 'visibility', nextVisibility);
  }

  /**
   * Reset the map canvas cursor to the default style.
   */
  resetCursor(): void {
    const canvas = this.map.getCanvasContainer();
    if (canvas) canvas.style.cursor = '';
  }

  /**
   * Toggle read-only mode for renderer-managed overlays.
   * Shared mode uses read-only and still needs polygon rendering to stay active.
   */
  setReadonly(isReadonly: boolean): void {
    if (this.isReadonly === isReadonly) return;
    this.isReadonly = isReadonly;
    if (isReadonly) {
      // Clear transient hover highlights when interaction is disabled.
      this.hoveredPointId = clearHoveredPointHelper(
        this.map,
        this.hoveredPointId,
        SourceId.Points,
      );
      this.hoveredDraftPointId = clearHoveredPointHelper(
        this.map,
        this.hoveredDraftPointId,
        SourceId.DraftPoints,
      );
      this.lastHoverState = undefined;
      this.resetCursor();
    }
  }

  /**
   * Dispose all renderer subscriptions and reset cached state.
   */
  destroy(): void {
    this.subscription.unsubscribe();
    this.hoveredPointId = null;
    this.hoveredDraftPointId = null;
    this.buildingGeoJson = getMapStoreyPolygonsFromStoreys(
      [],
      undefined,
      this.location,
    );
    if (this.map.getLayer(BUILDING_CLIP_LAYER_ID)) {
      this.map.removeLayer(BUILDING_CLIP_LAYER_ID);
    }
    if (this.map.getSource(BUILDING_CLIP_SOURCE_ID)) {
      this.map.removeSource(BUILDING_CLIP_SOURCE_ID);
    }
    this.detachStyleDataListener?.();
    this.detachStyleDataListener = undefined;
  }
}

/**
 * Structural comparison helper for {@link MapHoverState}.
 * @param a First state
 * @param b Second state
 * @returns true when both hover states describe the same target
 */
function areHoverStatesStructurallyEqual(
  a: MapHoverState | undefined,
  b: MapHoverState | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.type !== b.type) return false;
  switch (a.type) {
    case MapHoverStateType.Point:
      return (
        b.type === MapHoverStateType.Point &&
        a.index === b.index &&
        a.shapeId === b.shapeId
      );
    case MapHoverStateType.DraftPoint:
      return (
        b.type === MapHoverStateType.DraftPoint &&
        a.index === b.index &&
        a.shapeId === b.shapeId &&
        a.canComplete === b.canComplete
      );
    case MapHoverStateType.Line:
      return (
        b.type === MapHoverStateType.Line &&
        a.shapeId === b.shapeId &&
        a.startIndex === b.startIndex &&
        a.endIndex === b.endIndex &&
        Boolean(a.insertCandidateActive) === Boolean(b.insertCandidateActive)
      );
    case MapHoverStateType.DraftLine:
      return (
        b.type === MapHoverStateType.DraftLine &&
        a.startIndex === b.startIndex &&
        a.endIndex === b.endIndex
      );
    case MapHoverStateType.Shape:
      return b.type === MapHoverStateType.Shape && a.shapeId === b.shapeId;
    default:
      return false;
  }
}
